import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import { TabNavigation } from '../components/TabNavigation'
import { useAuthStore } from '../hooks/useAuthStore'
import { 
  CheckCircleIcon, 
  XMarkIcon, 
  ClockIcon, 
  EyeIcon,
  BanknotesIcon,
  ArrowRightOnRectangleIcon,
  UserIcon,
  CalendarDaysIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline'

interface PaymentSlip {
  id: string
  invoiceId: string
  studentId: string
  studentName: string
  amount: number
  uploadedAt: string
  slipImageUrl: string
  status: 'pending' | 'approved' | 'rejected'
  parentName: string
  rejectReason?: string
  verifiedAt?: string
  verifiedBy?: string
}

export function AdminPaymentsPage() {
  const navigate = useNavigate()
  const { logout } = useAuthStore()
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
  const [searchTerm, setSearchTerm] = useState('')

  // Mock payment slips data
  const [paymentSlips, setPaymentSlips] = useState<PaymentSlip[]>([
    {
      id: 'SLIP-001',
      invoiceId: 'INV-2568-09-001',
      studentId: 'STU-001',
      studentName: 'น้องใหม่',
      amount: 1600,
      uploadedAt: '2024-08-17T14:30:00Z',
      slipImageUrl: '/mock/slip1.jpg',
      status: 'pending',
      parentName: 'คุณสมศรี'
    },
    {
      id: 'SLIP-002',
      invoiceId: 'INV-2568-09-002',
      studentId: 'STU-002',
      studentName: 'น้องพิณ',
      amount: 1600,
      uploadedAt: '2024-08-17T13:15:00Z',
      slipImageUrl: '/mock/slip2.jpg',
      status: 'pending',
      parentName: 'คุณมาลี'
    },
    {
      id: 'SLIP-003',
      invoiceId: 'INV-2568-08-015',
      studentId: 'STU-003',
      studentName: 'น้องแนน',
      amount: 1600,
      uploadedAt: '2024-08-16T16:45:00Z',
      slipImageUrl: '/mock/slip3.jpg',
      status: 'approved',
      parentName: 'คุณจิรา',
      verifiedAt: '2024-08-16T17:00:00Z',
      verifiedBy: 'ผู้ดูแลระบบ'
    },
    {
      id: 'SLIP-004',
      invoiceId: 'INV-2568-08-020',
      studentId: 'STU-004',
      studentName: 'น้องมิ้ง',
      amount: 1600,
      uploadedAt: '2024-08-15T10:20:00Z',
      slipImageUrl: '/mock/slip4.jpg',
      status: 'rejected',
      parentName: 'คุณสุดา',
      rejectReason: 'จำนวนเงินไม่ตรงกับใบแจ้งหนี้',
      verifiedAt: '2024-08-15T11:00:00Z',
      verifiedBy: 'ผู้ดูแลระบบ'
    }
  ])

  const formatThaiBaht = (amount: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  const formatThaiDateTime = (dateStr: string) => {
    const date = new Date(dateStr)
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleApprove = (slip: PaymentSlip) => {
    setPaymentSlips(prev => prev.map(s => 
      s.id === slip.id 
        ? { 
            ...s, 
            status: 'approved', 
            verifiedAt: new Date().toISOString(),
            verifiedBy: 'ผู้ดูแลระบบ'
          }
        : s
    ))
  }


  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const handleViewSlip = (slip: PaymentSlip) => {
    navigate(`/admin/payments/${slip.id}/verify`, { 
      state: { slip } 
    })
  }

  // Filter slips based on status and search term
  const filteredSlips = paymentSlips
    .filter(slip => filter === 'all' || slip.status === filter)
    .filter(slip => 
      searchTerm === '' || 
      slip.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      slip.parentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      slip.invoiceId.toLowerCase().includes(searchTerm.toLowerCase())
    )

  const getStatusConfig = (status: PaymentSlip['status']) => {
    const configs = {
      pending: {
        color: 'bg-yellow-100 text-yellow-800',
        icon: ClockIcon,
        label: 'รอตรวจสอบ'
      },
      approved: {
        color: 'bg-green-100 text-green-800',
        icon: CheckCircleIcon,
        label: 'อนุมัติแล้ว'
      },
      rejected: {
        color: 'bg-red-100 text-red-800',
        icon: XMarkIcon,
        label: 'ไม่อนุมัติ'
      }
    }
    return configs[status]
  }

  const statusCounts = {
    all: paymentSlips.length,
    pending: paymentSlips.filter(s => s.status === 'pending').length,
    approved: paymentSlips.filter(s => s.status === 'approved').length,
    rejected: paymentSlips.filter(s => s.status === 'rejected').length
  }


  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="ตรวจสอบการชำระเงิน"
        showBackButton={true}
        actions={
          <button
            onClick={handleLogout}
            className="text-gray-600 hover:text-red-600 p-2 rounded-lg hover:bg-red-50 transition-colors"
            title="ออกจากระบบ"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5" />
          </button>
        }
      />

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-yellow-600">{statusCounts.pending}</div>
            <div className="text-sm text-gray-600">รอตรวจสอบ</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{statusCounts.approved}</div>
            <div className="text-sm text-gray-600">อนุมัติแล้ว</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-red-600">{statusCounts.rejected}</div>
            <div className="text-sm text-gray-600">ไม่อนุมัติ</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{statusCounts.all}</div>
            <div className="text-sm text-gray-600">ทั้งหมด</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Status Filter */}
            <div className="flex space-x-2">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    filter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status === 'all' ? 'ทั้งหมด' :
                   status === 'pending' ? 'รอตรวจสอบ' :
                   status === 'approved' ? 'อนุมัติแล้ว' : 'ไม่อนุมัติ'}
                  {statusCounts[status] > 0 && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      filter === status ? 'bg-white bg-opacity-20' : 'bg-gray-200'
                    }`}>
                      {statusCounts[status]}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาชื่อนักเรียน, ผู้ปกครอง หรือเลขใบแจ้งหนี้..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Payment Slips List */}
        <div className="space-y-4">
          {filteredSlips.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <BanknotesIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">ไม่พบข้อมูล</h3>
              <p className="text-gray-500">
                {filter === 'pending' ? 'ไม่มีการชำระเงินที่รอตรวจสอบ' : 
                 `ไม่พบข้อมูลการชำระเงินสถานะ "${filter === 'all' ? 'ทั้งหมด' : filter}"`}
              </p>
            </div>
          ) : (
            filteredSlips.map((slip) => {
              const statusConfig = getStatusConfig(slip.status)
              const StatusIcon = statusConfig.icon

              return (
                <div key={slip.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {slip.invoiceId}
                        </h3>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <UserIcon className="h-4 w-4 mr-1" />
                          <span>{slip.studentName} • ผู้ปกครอง: {slip.parentName}</span>
                        </div>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <CalendarDaysIcon className="h-4 w-4 mr-1" />
                          <span>ส่งสลิปเมื่อ: {formatThaiDateTime(slip.uploadedAt)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-3">
                        <div className="text-right">
                          <div className="text-lg font-bold text-blue-600">
                            {formatThaiBaht(slip.amount)}
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Additional Info for processed slips */}
                    {slip.verifiedAt && (
                      <div className="text-sm text-gray-500 mb-3">
                        ตรวจสอบเมื่อ: {formatThaiDateTime(slip.verifiedAt)} โดย {slip.verifiedBy}
                        {slip.rejectReason && (
                          <div className="text-red-600 mt-1">
                            เหตุผล: {slip.rejectReason}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-end space-x-2 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => handleViewSlip(slip)}
                        className="text-blue-600 hover:text-blue-800 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors flex items-center"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        ดูสลิป
                      </button>
                      
                      {slip.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleApprove(slip)}
                            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-1" />
                            อนุมัติ
                          </button>
                          <button
                            onClick={() => navigate('/admin/payments/reject', { 
                              state: { slip } 
                            })}
                            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                          >
                            <XMarkIcon className="h-4 w-4 mr-1" />
                            ไม่อนุมัติ
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      <TabNavigation activeTab="payments" />
    </div>
  )
}